import React, { useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL } from './api';
import { DARK_MODE_EVENT, getDarkMode, setDarkMode } from './darkMode';

const PRESENCE_STATUS_CHOICES = [
    { key: 'online', label: 'Online' },
    { key: 'away', label: 'Away' },
    { key: 'busy', label: 'Busy' },
    { key: 'offline', label: 'Appear Offline' }
];

function normalizePresenceStatus(raw) {
    const s = String(raw || '').trim().toLowerCase();
    if (!s) return 'online';
    if (s === 'online') return 'online';
    if (s === 'away') return 'away';
    if (s === 'busy') return 'busy';
    if (s === 'offline') return 'offline';
    return 'online';
}

function getAuthHeader() {
    try {
        const token = localStorage.getItem('monsurv_auth_token') || '';
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
        return {};
    }
}

async function fetchPresenceStatus() {
    const res = await fetch(`${API_BASE_URL}/auth/status`, {
        method: 'GET',
        headers: { ...getAuthHeader() }
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
    return normalizePresenceStatus(json?.status);
}

async function setPresenceStatus(status) {
    const res = await fetch(`${API_BASE_URL}/auth/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ status })
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
    return normalizePresenceStatus(json?.status);
}

function safeInitials(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return 'U';
    const a = parts[0]?.[0] || '';
    const b = parts.length > 1 ? (parts[parts.length - 1]?.[0] || '') : (parts[0]?.[1] || '');
    const out = `${a}${b}`.trim().toUpperCase();
    return out || 'U';
}

function presenceDotStyle(status, { compact } = {}) {
    const s = normalizePresenceStatus(status);
    const size = compact ? 9 : 10;
    const borderWidth = 2;

    if (s === 'offline') {
        return {
            width: size,
            height: size,
            borderRadius: '50%',
            background: 'transparent',
            border: `${borderWidth}px solid #9ca3af`
        };
    }

    const fill =
        s === 'busy' ? '#991b1b'
            : (s === 'away' ? '#eab308' : '#047857');

    return {
        width: size,
        height: size,
        borderRadius: '50%',
        background: fill,
        border: `${borderWidth}px solid #ffffff`
    };
}

function presenceTone(status) {
    const s = normalizePresenceStatus(status);
    if (s === 'busy') return { color: '#991b1b', background: 'rgba(220, 38, 38, 0.12)', border: 'rgba(220, 38, 38, 0.18)' };
    if (s === 'away') return { color: '#b45309', background: 'rgba(245, 158, 11, 0.14)', border: 'rgba(245, 158, 11, 0.2)' };
    if (s === 'offline') return { color: '#64748b', background: 'rgba(148, 163, 184, 0.14)', border: 'rgba(148, 163, 184, 0.22)' };
    return { color: '#047857', background: 'rgba(16, 185, 129, 0.14)', border: 'rgba(16, 185, 129, 0.2)' };
}

export default function AccountMenu({
    displayName,
    position,
    role,
    profilePhotoUrl,
    menuVariant = 'full',
    chromeVariant = 'default',
    compact = false,
    onOpenSettingsSection,
    onChangePassword,
    onSignOut
}) {
    const [open, setOpen] = useState(false);
    const [dark, setDark] = useState(() => getDarkMode());
    const rootRef = useRef(null);
    const [photoOk, setPhotoOk] = useState(true);

    const [presenceOpen, setPresenceOpen] = useState(false);
    const [presenceStatus, setPresenceStatusState] = useState('online');
    const [presenceLoading, setPresenceLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const status = await fetchPresenceStatus();
                if (!cancelled) setPresenceStatusState(status);
            } catch {
                // ignore
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const initials = useMemo(() => safeInitials(displayName), [displayName]);
    const photoSrc = useMemo(() => {
        const raw = String(profilePhotoUrl || '').trim();
        if (!raw) return '';
        if (/^https?:\/\//i.test(raw)) return raw;
        return `${API_BASE_URL}${raw.startsWith('/') ? raw : `/${raw}`}`;
    }, [profilePhotoUrl]);

    useEffect(() => {
        setPhotoOk(true);
    }, [photoSrc]);

    useEffect(() => {
        function syncDarkMode(event) {
            const next = typeof event?.detail?.dark === 'boolean' ? event.detail.dark : getDarkMode();
            setDark(next);
        }

        try {
            window.addEventListener(DARK_MODE_EVENT, syncDarkMode);
            return () => window.removeEventListener(DARK_MODE_EVENT, syncDarkMode);
        } catch {
            return undefined;
        }
    }, []);

    useEffect(() => {
        function onDocMouseDown(e) {
            if (!open) return;
            const el = rootRef.current;
            if (!el) return;
            if (el.contains(e.target)) return;
            setOpen(false);
        }

        function onKeyDown(e) {
            if (!open) return;
            if (e.key === 'Escape') setOpen(false);
        }

        document.addEventListener('mousedown', onDocMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onDocMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    useEffect(() => {
        if (!open) {
            setPresenceOpen(false);
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                setPresenceLoading(true);
                const status = await fetchPresenceStatus();
                if (!cancelled) setPresenceStatusState(status);
            } catch {
                // ignore (best-effort)
            } finally {
                if (!cancelled) setPresenceLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open]);

    const presenceLabel = useMemo(() => {
        return PRESENCE_STATUS_CHOICES.find((choice) => choice.key === presenceStatus)?.label || 'Online';
    }, [presenceStatus]);

    const roleLabel = position || (role ? String(role).toUpperCase() : '—');
    const isHeaderVariant = chromeVariant === 'header';
    const activePresenceTone = presenceTone(presenceStatus);
    const colors = useMemo(() => {
        if (dark) {
            return {
                triggerBg: isHeaderVariant
                    ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.92) 0%, rgba(22, 32, 51, 0.84) 100%)'
                    : 'transparent',
                triggerBorder: isHeaderVariant ? '1px solid rgba(226, 232, 240, 0.12)' : '1px solid rgba(226, 232, 240, 0.16)',
                triggerShadow: isHeaderVariant ? 'inset 0 1px 0 rgba(255,255,255,0.04), 0 12px 28px rgba(2, 6, 23, 0.28)' : 'none',
                triggerText: '#e5e7eb',
                secondaryText: '#94a3b8',
                panelBg: isHeaderVariant
                    ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.98) 0%, rgba(17, 24, 39, 0.96) 100%)'
                    : 'linear-gradient(180deg, rgba(15, 23, 42, 0.98) 0%, rgba(17, 24, 39, 0.96) 100%)',
                panelBorder: isHeaderVariant ? '1px solid rgba(226, 232, 240, 0.12)' : '1px solid rgba(226, 232, 240, 0.12)',
                panelShadow: isHeaderVariant ? '0 22px 48px rgba(2, 6, 23, 0.42)' : '0 18px 40px rgba(2, 6, 23, 0.34)',
                panelHeaderBg: 'linear-gradient(180deg, rgba(96, 165, 250, 0.08) 0%, rgba(255,255,255,0.02) 100%)',
                divider: 'rgba(226, 232, 240, 0.08)',
                cardBg: 'linear-gradient(180deg, rgba(17, 24, 39, 0.96) 0%, rgba(30, 41, 59, 0.92) 100%)',
                cardActiveBg: 'linear-gradient(180deg, rgba(30, 64, 175, 0.26) 0%, rgba(30, 41, 59, 0.94) 100%)',
                cardDangerBg: 'linear-gradient(180deg, rgba(127, 29, 29, 0.34) 0%, rgba(69, 10, 10, 0.34) 100%)',
                cardBorder: 'rgba(226, 232, 240, 0.08)',
                cardActiveBorder: 'rgba(96, 165, 250, 0.22)',
                cardDangerBorder: 'rgba(248, 113, 113, 0.26)',
                cardText: '#e5e7eb',
                cardMuted: '#94a3b8',
                dangerText: '#fca5a5',
                sectionTitle: '#94a3b8',
                switchTrackOn: '#60a5fa',
                switchTrackOff: '#334155',
                switchKnob: '#f8fafc'
            };
        }

        return {
            triggerBg: isHeaderVariant
                ? 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(240,246,251,0.72) 100%)'
                : 'transparent',
            triggerBorder: isHeaderVariant ? '1px solid rgba(255,255,255,0.58)' : '1px solid rgba(15, 23, 42, 0.12)',
            triggerShadow: isHeaderVariant ? 'inset 0 1px 0 rgba(255,255,255,0.68), 0 10px 26px rgba(15, 23, 42, 0.08)' : 'none',
            triggerText: '#0f172a',
            secondaryText: '#64748b',
            panelBg: isHeaderVariant
                ? 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(244,248,252,0.94) 100%)'
                : '#fff',
            panelBorder: isHeaderVariant ? '1px solid rgba(255,255,255,0.62)' : '1px solid rgba(15, 23, 42, 0.10)',
            panelShadow: isHeaderVariant ? '0 18px 44px rgba(15, 23, 42, 0.16)' : '0 14px 40px rgba(15, 23, 42, 0.18)',
            panelHeaderBg: 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.08) 100%)',
            divider: 'rgba(15, 23, 42, 0.06)',
            cardBg: 'linear-gradient(180deg, rgba(255, 255, 255, 0.92) 0%, rgba(248, 250, 252, 0.86) 100%)',
            cardActiveBg: 'linear-gradient(180deg, rgba(239, 246, 255, 0.96) 0%, rgba(219, 234, 254, 0.86) 100%)',
            cardDangerBg: 'linear-gradient(180deg, rgba(254, 242, 242, 0.96) 0%, rgba(254, 226, 226, 0.88) 100%)',
            cardBorder: 'rgba(15, 23, 42, 0.08)',
            cardActiveBorder: 'rgba(37, 99, 235, 0.18)',
            cardDangerBorder: 'rgba(220, 38, 38, 0.18)',
            cardText: '#0f172a',
            cardMuted: '#64748b',
            dangerText: '#991b1b',
            sectionTitle: '#64748b',
            switchTrackOn: '#243242',
            switchTrackOff: '#cbd5e1',
            switchKnob: '#ffffff'
        };
    }, [dark, isHeaderVariant]);

    function panelCardStyle({ active = false, danger = false } = {}) {
        return {
            width: '100%',
            padding: compact ? '10px 11px' : '11px 12px',
            textAlign: 'left',
            borderRadius: 14,
            border: danger
                ? `1px solid ${colors.cardDangerBorder}`
                : (active ? `1px solid ${colors.cardActiveBorder}` : `1px solid ${colors.cardBorder}`),
            background: danger
                ? colors.cardDangerBg
                : (active
                    ? colors.cardActiveBg
                    : colors.cardBg),
            boxShadow: danger
                ? (dark ? '0 12px 28px rgba(127, 29, 29, 0.18)' : '0 10px 24px rgba(220, 38, 38, 0.08)')
                : (dark ? '0 12px 28px rgba(2, 6, 23, 0.18)' : '0 10px 24px rgba(15, 23, 42, 0.06)'),
            cursor: 'pointer',
            color: danger ? colors.dangerText : colors.cardText
        };
    }

    function sectionTitleStyle() {
        return {
            fontSize: compact ? 10.5 : 11,
            fontWeight: 800,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: colors.sectionTitle,
            marginBottom: compact ? 7 : 8
        };
    }

    return (
        <div ref={rootRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
                type="button"
                onClick={() => {
                    setDark(getDarkMode());
                    setOpen((v) => !v);
                }}
                aria-haspopup="menu"
                aria-expanded={open ? 'true' : 'false'}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: compact ? 0 : 10,
                    minHeight: compact ? 32 : 42,
                    background: colors.triggerBg,
                    border: colors.triggerBorder,
                    boxShadow: colors.triggerShadow,
                    padding: compact ? (isHeaderVariant ? '2px' : 0) : (isHeaderVariant ? '5px 8px 5px 5px' : '6px 10px'),
                    borderRadius: 999,
                    cursor: 'pointer',
                    color: colors.triggerText
                }}
                aria-label={compact ? 'Account menu' : undefined}
            >
                <div style={{
                    position: 'relative',
                    width: compact ? 28 : 32,
                    height: compact ? 28 : 32,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    background: 'linear-gradient(135deg, #1d4ed8, #243242)',
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: compact ? 11 : 12,
                    letterSpacing: 0.4,
                    boxShadow: isHeaderVariant ? '0 8px 18px rgba(29, 78, 216, 0.18)' : 'none'
                }}>
                    {photoSrc && photoOk ? (
                        <img
                            src={photoSrc}
                            alt=""
                            onError={() => setPhotoOk(false)}
                            style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', display: 'block' }}
                        />
                    ) : initials}

                    <span
                        aria-hidden="true"
                        style={{
                            position: 'absolute',
                            right: compact ? -1 : -1,
                            bottom: compact ? -1 : -1,
                            ...presenceDotStyle(presenceStatus, { compact })
                        }}
                    />
                </div>
                {compact ? null : (
                    <div style={{ textAlign: 'left', lineHeight: 1.1, minWidth: 0, maxWidth: 240 }}>
                        <div style={{ fontWeight: 800, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: colors.triggerText, letterSpacing: '-0.01em' }}>
                            {displayName || 'Account'}
                        </div>
                        <div style={{ fontSize: 11.5, color: colors.secondaryText, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {`${roleLabel} · ${presenceLabel}`}
                        </div>
                    </div>
                )}
            </button>

            {open ? (
                <div
                    role="menu"
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        right: 0,
                        width: compact ? 256 : 340,
                        maxWidth: 'calc(100vw - 24px)',
                        background: colors.panelBg,
                        border: colors.panelBorder,
                        borderRadius: compact ? 5 : 6,
                        boxShadow: colors.panelShadow,
                        backdropFilter: isHeaderVariant ? 'blur(14px)' : undefined,
                        overflow: 'hidden',
                        zIndex: 6000
                    }}
                >
                    <div
                        style={{
                            padding: compact ? '11px' : '14px',
                            borderBottom: `1px solid ${colors.divider}`,
                            background: colors.panelHeaderBg
                        }}
                    >
                        <div style={{ display: 'flex', gap: compact ? 10 : 12, alignItems: 'center' }}>
                            <div
                                style={{
                                    position: 'relative',
                                    width: compact ? 42 : 52,
                                    height: compact ? 42 : 52,
                                    borderRadius: '50%',
                                    display: 'grid',
                                    placeItems: 'center',
                                    background: 'linear-gradient(135deg, #1d4ed8, #243242)',
                                    color: '#fff',
                                    fontWeight: 800,
                                    fontSize: compact ? 14 : 16,
                                    letterSpacing: 0.4,
                                    boxShadow: '0 14px 28px rgba(29, 78, 216, 0.18)'
                                }}
                            >
                                {photoSrc && photoOk ? (
                                    <img
                                        src={photoSrc}
                                        alt=""
                                        onError={() => setPhotoOk(false)}
                                        style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', display: 'block' }}
                                    />
                                ) : initials}

                                <span
                                    aria-hidden="true"
                                    style={{
                                        position: 'absolute',
                                        right: -1,
                                        bottom: -1,
                                        ...presenceDotStyle(presenceStatus, { compact: false })
                                    }}
                                />
                            </div>

                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div
                                    style={{
                                        fontWeight: 900,
                                        fontSize: compact ? 13 : 15,
                                        color: colors.cardText,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        letterSpacing: '-0.02em'
                                    }}
                                >
                                    {displayName || 'Signed in'}
                                </div>
                                <div
                                    style={{
                                        marginTop: 3,
                                        fontSize: compact ? 11 : 12,
                                        color: colors.cardMuted,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}
                                >
                                    {roleLabel}
                                </div>
                                <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 7, padding: '5px 9px', borderRadius: 999, background: activePresenceTone.background, border: `1px solid ${activePresenceTone.border}`, color: activePresenceTone.color, fontSize: 11, fontWeight: 800 }}>
                                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: activePresenceTone.color }} />
                                    {presenceLabel}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{ padding: compact ? '10px 11px 0 11px' : '13px 14px 0 14px' }}>
                        <div style={sectionTitleStyle()}>Account</div>
                        <div style={{ display: 'grid', gap: 8 }}>
                            <button
                                type="button"
                                style={panelCardStyle()}
                                onClick={() => {
                                    setOpen(false);
                                    onOpenSettingsSection?.('account');
                                }}
                            >
                                <div style={{ fontWeight: 800, fontSize: compact ? 12 : 13 }}>Account & Preferences</div>
                                <div style={{ marginTop: 3, fontSize: compact ? 10.5 : 11.5, color: colors.cardMuted }}>Profile details, security and personal settings</div>
                            </button>

                            <button
                                type="button"
                                style={panelCardStyle({ active: presenceOpen })}
                                onClick={() => setPresenceOpen((v) => !v)}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                    <div>
                                        <div style={{ fontWeight: 800, fontSize: compact ? 12 : 13 }}>Online Status</div>
                                        <div style={{ marginTop: 3, fontSize: compact ? 10.5 : 11.5, color: colors.cardMuted }}>
                                            {presenceLoading ? 'Updating presence…' : `Currently ${presenceLabel}`}
                                        </div>
                                    </div>
                                    <div style={{ fontSize: 16, color: colors.cardMuted, transform: presenceOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 160ms ease' }}>
                                        ▾
                                    </div>
                                </div>
                            </button>
                        </div>
                    </div>

                    {presenceOpen ? (
                        <div style={{ padding: compact ? '8px 11px 0 11px' : '10px 14px 0 14px' }}>
                            <div style={{ display: 'grid', gap: 7 }}>
                                {PRESENCE_STATUS_CHOICES.map((choice) => {
                                    const active = choice.key === presenceStatus;
                                    const tone = presenceTone(choice.key);
                                    return (
                                        <button
                                            key={choice.key}
                                            type="button"
                                            style={{
                                                width: '100%',
                                                padding: compact ? '9px 10px' : '10px 11px',
                                                textAlign: 'left',
                                                borderRadius: 6,
                                                border: active ? `1px solid ${tone.border}` : `1px solid ${colors.cardBorder}`,
                                                background: active ? tone.background : (dark ? 'rgba(17, 24, 39, 0.82)' : 'rgba(255,255,255,0.78)'),
                                                boxShadow: active ? (dark ? '0 12px 28px rgba(2, 6, 23, 0.18)' : '0 10px 24px rgba(15, 23, 42, 0.06)') : 'none',
                                                cursor: 'pointer',
                                                fontSize: compact ? 12 : 13,
                                                color: colors.cardText,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 10
                                            }}
                                            onClick={async () => {
                                                try {
                                                    setPresenceLoading(true);
                                                    const next = await setPresenceStatus(choice.key);
                                                    setPresenceStatusState(next);
                                                } catch {
                                                    // ignore
                                                } finally {
                                                    setPresenceLoading(false);
                                                }
                                            }}
                                        >
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}>
                                                <span style={{ width: 9, height: 9, borderRadius: '50%', background: tone.color, border: choice.key === 'offline' ? '2px solid #94a3b8' : 'none' }} />
                                                {choice.label}
                                            </span>
                                            {active ? <span style={{ fontSize: 11, fontWeight: 800, color: tone.color }}>Active</span> : null}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}

                    {menuVariant === 'full' ? (
                        <>
                            <div style={{ padding: compact ? '12px 11px 0 11px' : '14px 14px 0 14px' }}>
                                <div style={sectionTitleStyle()}>Workspace</div>
                                <div style={{ display: 'grid', gap: 8 }}>
                                    <button
                                        type="button"
                                        style={panelCardStyle()}
                                        onClick={() => {
                                            setOpen(false);
                                            onOpenSettingsSection?.('calendar');
                                        }}
                                    >
                                        <div style={{ fontWeight: 800, fontSize: compact ? 12 : 13 }}>Calendar Sharing</div>
                                        <div style={{ marginTop: 3, fontSize: compact ? 10.5 : 11.5, color: colors.cardMuted }}>Connections, sync status and availability sharing</div>
                                    </button>

                                    <button
                                        type="button"
                                        style={panelCardStyle()}
                                        onClick={() => {
                                            setOpen(false);
                                            onOpenSettingsSection?.('appearance');
                                        }}
                                    >
                                        <div style={{ fontWeight: 800, fontSize: compact ? 12 : 13 }}>Appearance</div>
                                        <div style={{ marginTop: 3, fontSize: compact ? 10.5 : 11.5, color: colors.cardMuted }}>Theme, display settings and interface preferences</div>
                                    </button>

                                    <button
                                        type="button"
                                        style={panelCardStyle()}
                                        onClick={() => {
                                            setOpen(false);
                                            onChangePassword?.();
                                        }}
                                    >
                                        <div style={{ fontWeight: 800, fontSize: compact ? 12 : 13 }}>Change Password</div>
                                        <div style={{ marginTop: 3, fontSize: compact ? 10.5 : 11.5, color: colors.cardMuted }}>Update your credentials and account access</div>
                                    </button>
                                </div>
                            </div>

                            <div style={{ padding: compact ? '12px 11px 0 11px' : '14px 14px 0 14px' }}>
                                <div style={sectionTitleStyle()}>Appearance</div>
                                <div style={{ padding: compact ? '10px 11px' : '11px 12px', borderRadius: 7, border: `1px solid ${colors.cardBorder}`, background: colors.cardBg, boxShadow: dark ? '0 12px 28px rgba(2, 6, 23, 0.18)' : '0 10px 24px rgba(15, 23, 42, 0.06)' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, cursor: 'pointer' }}>
                                        <span style={{ minWidth: 0 }}>
                                            <span style={{ display: 'block', fontSize: compact ? 12 : 13, color: colors.cardText, fontWeight: 800 }}>Dark mode</span>
                                            <span style={{ display: 'block', marginTop: 3, fontSize: compact ? 10.5 : 11.5, color: colors.cardMuted }}>Toggle the interface theme for the entire workspace</span>
                                        </span>
                                        <span style={{ position: 'relative', flexShrink: 0, width: 42, height: 24, borderRadius: 999, background: dark ? colors.switchTrackOn : colors.switchTrackOff, transition: 'background 160ms ease' }}>
                                            <span style={{ position: 'absolute', top: 3, left: dark ? 21 : 3, width: 18, height: 18, borderRadius: '50%', background: colors.switchKnob, boxShadow: dark ? '0 2px 8px rgba(2, 6, 23, 0.36)' : '0 2px 6px rgba(15, 23, 42, 0.18)', transition: 'left 160ms ease' }} />
                                            <input
                                                type="checkbox"
                                                checked={Boolean(dark)}
                                                onChange={(e) => {
                                                    const next = Boolean(e.target.checked);
                                                    setDark(next);
                                                    setDarkMode(next);
                                                }}
                                                style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                                            />
                                        </span>
                                    </label>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div style={{ padding: compact ? '10px 11px 0 11px' : '12px 14px 0 14px' }} />
                    )}

                    <div style={{ padding: compact ? '12px 11px 12px 11px' : '14px', marginTop: compact ? 2 : 4 }}>
                        <button
                            type="button"
                            style={panelCardStyle({ danger: true })}
                            onClick={() => {
                                setOpen(false);
                                onSignOut?.();
                            }}
                        >
                            <div style={{ fontWeight: 900, fontSize: compact ? 12 : 13 }}>Sign Out</div>
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
